import type { OperationVariables, TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Empty } from '@sorare/core/src/components/cards/Empty';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_CONTENDER_CONTENDER } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { glossary } from '@sorare/core/src/lib/glossary';
import { mobileAndAbove } from '@sorare/core/src/style/mediaQuery';

import { Contender } from 'components/lineup/Contender';

import type { UserFixtureResults_so5Fixture } from './__generated__/index.graphql';

const Root = styled.div`
  padding: 0 var(--double-unit) var(--quadruple-unit);
  position: relative;
  isolation: isolate;
`;
const Grid = styled.div`
  display: grid;
  max-width: 100%;
  gap: var(--triple-unit);
  grid-template-columns: minmax(0, 1fr);
  @media ${mobileAndAbove} {
    grid-template-columns: repeat(
      auto-fill,
      minmax(clamp(340px, calc(${100 / 3}% - var(--double-unit)), 550px), 1fr)
        /** Between 340px and 1/3 and 550px to try to fit 3 items per columns */
    );
  }
`;

type Props = {
  so5Fixture?: UserFixtureResults_so5Fixture | null;
  loadMoreUserResults: (
    reload: boolean,
    loadMoreVariables: Partial<OperationVariables>,
    forceConnection?: string | undefined
  ) => void;
  moreResultsLoading?: boolean;
};
export const UserFixtureResults = ({
  so5Fixture,
  loadMoreUserResults,
  moreResultsLoading,
}: Props) => {
  const { formatMessage } = useIntl();
  const { generateSportContextPath } = useSportContext();
  const track = useEvents();

  const { so5LeaderboardContenders } = so5Fixture?.userFixtureResults || {};

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMoreUserResults(
        false,
        { cursor: so5LeaderboardContenders?.pageInfo.endCursor },
        'So5LeaderboardContenderConnection'
      );
    },
    !!so5LeaderboardContenders?.pageInfo.hasNextPage,
    moreResultsLoading
  );

  if (!so5Fixture) {
    return null;
  }

  const contenders = so5LeaderboardContenders?.nodes || [];

  return (
    <Root>
      {contenders.length === 0 ? (
        <Empty
          title={formatMessage(glossary.quietWeek)}
          description={formatMessage(glossary.noTeamForThisWeek)}
        />
      ) : (
        <Grid>
          {contenders.map(contender => {
            return (
              <Contender
                key={contender.slug}
                anchorId={contender.slug}
                so5LeaderboardContender={contender}
                to={generateSportContextPath(
                  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_CONTENDER_CONTENDER,
                  {
                    params: {
                      fixture: so5Fixture.slug,
                      eventType: so5Fixture.type.toLowerCase(),
                      contender: contender.slug,
                    },
                  }
                )}
                onClick={() => track('Click Lineup Details My Circle', {})}
                lineupStyle="forced_inline"
              />
            );
          })}
        </Grid>
      )}
      <InfiniteScrollLoader />
    </Root>
  );
};

UserFixtureResults.fragments = {
  so5Fixture: gql`
    fragment UserFixtureResults_so5Fixture on So5Fixture {
      slug
      id
      type
      userFixtureResults(userSlug: $user) {
        slug
        so5LeaderboardContenders(first: 10, after: $cursor) {
          nodes {
            slug
            so5Leaderboard {
              slug
            }
            ...Contender_so5LeaderboardContender
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
    ${Contender.fragments.so5LeaderboardContender}
  ` as TypedDocumentNode<UserFixtureResults_so5Fixture>,
};
